import { FC, useEffect, useState } from 'react'
import { useLocaleState, useTranslate } from 'react-admin'
import { Box, Card, CardContent, Switch, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import LanguageIcon from '@mui/icons-material/Language'
import TuneIcon from '@mui/icons-material/Tune'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { themeColor } from '../layout/themes'
import { LOCALE_KEY } from '../providers'
import { getUser } from '../oidc'

type Section = 'language' | 'appearance' | 'about'

const NAV_ITEMS: { key: Section; icon: JSX.Element }[] = [
  { key: 'language', icon: <LanguageIcon /> },
  { key: 'appearance', icon: <TuneIcon /> },
  { key: 'about', icon: <InfoOutlinedIcon /> },
]

const LANGUAGES = [
  { code: 'en', label: 'English', native: 'English (US)', flag: '🇺🇸' },
  { code: 'nl', label: 'Nederlands', native: 'Dutch (NL)', flag: '🇳🇱' },
  { code: 'no', label: 'Norsk', native: 'Norwegian (NO)', flag: '🇳🇴' },
]

const FILTER_ASIDE_KEY = 'sa_filter_aside_open'
const ROWS_PER_PAGE_KEY = 'sa_rows_per_page' // keep in sync with utils/usePerPage.ts

const LanguageSection: FC = () => {
  const translate = useTranslate()
  const [, setLocale] = useLocaleState()
  const currentLocale = localStorage.getItem(LOCALE_KEY) || 'en'

  const handleChange = (code: string) => {
    localStorage.setItem(LOCALE_KEY, code)
    setLocale(code)
    window.location.reload()
  }

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {translate('settings.languageTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('settings.languageDescription')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {LANGUAGES.map(({ code, label, native, flag }) => {
          const active = currentLocale === code
          return (
            <Box
              key={code}
              onClick={() => handleChange(code)}
              sx={{
                'flex': 1,
                'display': 'flex',
                'flexDirection': 'column',
                'alignItems': 'center',
                'gap': 0.5,
                'p': 2,
                'borderRadius': '10px',
                'border': '2px solid',
                'borderColor': active ? themeColor.blue : themeColor.slate10,
                'bgcolor': active ? themeColor.blue10 : '#fff',
                'cursor': 'pointer',
                'transition': 'all 0.15s ease',
                'position': 'relative',
                '&:hover': {
                  borderColor: active ? themeColor.blue : themeColor.slate20,
                  bgcolor: active ? themeColor.blue10 : themeColor.slate05,
                },
              }}
            >
              {active && (
                <CheckCircleIcon sx={{
                  position: 'absolute', top: 8, right: 8,
                  fontSize: 16, color: themeColor.blue,
                }} />
              )}
              <Typography sx={{ fontSize: 24 }}>{flag}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: active ? themeColor.blue : themeColor.black }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: themeColor.slate40 }}>
                {native}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </>
  )
}

const AppearanceSection: FC = () => {
  const translate = useTranslate()
  const [filtersOpen, setFiltersOpen] = useState(
    () => localStorage.getItem(FILTER_ASIDE_KEY) === 'true',
  )
  const [rowsPerPage, setRowsPerPage] = useState(
    () => parseInt(localStorage.getItem(ROWS_PER_PAGE_KEY) || '10', 10),
  )

  const handleFiltersToggle = () => {
    const next = !filtersOpen
    setFiltersOpen(next)
    localStorage.setItem(FILTER_ASIDE_KEY, String(next))
  }

  const handleRowsChange = (value: number) => {
    setRowsPerPage(value)
    localStorage.setItem(ROWS_PER_PAGE_KEY, String(value))
  }

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {translate('settings.appearanceTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('settings.appearanceDescription')}
      </Typography>

      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        p: 2, borderRadius: '10px', border: '1px solid', borderColor: themeColor.slate10, mb: 1.5,
      }}>
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {translate('settings.filtersOpen')}
          </Typography>
          <Typography sx={{ fontSize: 12, color: themeColor.slate40 }}>
            {translate('settings.filtersOpenHelp')}
          </Typography>
        </Box>
        <Switch checked={filtersOpen} onChange={handleFiltersToggle} size="small" />
      </Box>

      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1 }}>
          {translate('settings.rowsPerPage')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {[10, 25, 50].map((n) => (
            <Box
              key={n}
              onClick={() => handleRowsChange(n)}
              sx={{
                'px': 2.5,
                'py': 1,
                'borderRadius': '8px',
                'border': '2px solid',
                'borderColor': rowsPerPage === n ? themeColor.blue : themeColor.slate10,
                'bgcolor': rowsPerPage === n ? themeColor.blue10 : '#fff',
                'cursor': 'pointer',
                'fontSize': 14,
                'fontWeight': rowsPerPage === n ? 600 : 400,
                'color': rowsPerPage === n ? themeColor.blue : themeColor.slate60,
                'transition': 'all 0.15s ease',
                '&:hover': {
                  borderColor: rowsPerPage === n ? themeColor.blue : themeColor.slate20,
                },
              }}
            >
              {n}
            </Box>
          ))}
        </Box>
      </Box>
    </>
  )
}

const AboutSection: FC = () => {
  const translate = useTranslate()
  const [userInfo, setUserInfo] = useState<{ name?: string; email?: string }>({})

  useEffect(() => {
    void getUser().then((u) => {
      if (u) setUserInfo({ name: u.name, email: u.email })
    })
  }, [])

  return (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
        {translate('settings.aboutTitle')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {userInfo.name && (
          <InfoRow label={translate('settings.userName')} value={userInfo.name} />
        )}
        {userInfo.email && (
          <InfoRow label={translate('settings.userEmail')} value={userInfo.email} />
        )}
        <InfoRow label={translate('settings.appVersion')} value="1.0" />
      </Box>
    </>
  )
}

const InfoRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    p: 2, borderRadius: '10px', border: '1px solid', borderColor: themeColor.slate10,
  }}>
    <Typography sx={{ fontSize: 13, color: themeColor.slate40, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
)

const SectionContent: FC<{ section: Section }> = ({ section }) => {
  switch (section) {
    case 'language': return <LanguageSection />
    case 'appearance': return <AppearanceSection />
    case 'about': return <AboutSection />
  }
}

const Settings: FC = () => {
  const [section, setSection] = useState<Section>('language')
  const translate = useTranslate()

  return (
    <Root>
      <Box className={classes.header}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {translate('settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('settings.description')}
        </Typography>
      </Box>

      <Box className={classes.layout}>
        <nav className={classes.nav}>
          {NAV_ITEMS.map(({ key, icon }) => (
            <Box
              key={key}
              className={`${classes.navItem} ${section === key ? classes.navItemActive : ''}`}
              onClick={() => setSection(key)}
            >
              {icon}
              <span>{translate(`settings.section.${key}`)}</span>
            </Box>
          ))}
        </nav>

        <Box className={classes.content}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: themeColor.slate10 }}>
            <CardContent sx={{ p: 3 }}>
              <SectionContent section={section} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Root>
  )
}

const PREFIX = 'xxllncSettings'

const classes = {
  header: `${PREFIX}-header`,
  layout: `${PREFIX}-layout`,
  nav: `${PREFIX}-nav`,
  navItem: `${PREFIX}-navItem`,
  navItemActive: `${PREFIX}-navItemActive`,
  content: `${PREFIX}-content`,
}

const Root = styled('div')(() => ({
  padding: '16px 24px',
  maxWidth: 1000,

  [`& .${classes.header}`]: {
    marginBottom: 24,
  },
  [`& .${classes.layout}`]: {
    display: 'flex',
    gap: 24,
    alignItems: 'flex-start',
  },
  [`& .${classes.nav}`]: {
    width: 200,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    position: 'sticky',
    top: 80,
  },
  [`& .${classes.navItem}`]: {
    'display': 'flex',
    'alignItems': 'center',
    'gap': 10,
    'padding': '10px 14px',
    'borderRadius': 8,
    'cursor': 'pointer',
    'fontSize': 14,
    'fontWeight': 500,
    'color': themeColor.slate60,
    'transition': 'all 0.15s ease',
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
    '&:hover': {
      backgroundColor: themeColor.slate05,
      color: themeColor.black,
    },
  },
  [`& .${classes.navItemActive}`]: {
    'backgroundColor': themeColor.blue10,
    'color': themeColor.blue,
    'fontWeight': 600,
    '& .MuiSvgIcon-root': {
      color: themeColor.blue,
    },
  },
  [`& .${classes.content}`]: {
    flex: 1,
    minWidth: 0,
  },
}))

export default Settings
